// REST-Client für die persistenten Review-Decisions im maco-runner.
// Spiegelt /api/review/decisions/* aus routes_review.py.

import type { ReviewDecision } from "@/types/tickets";

export type ServerDecision = ReviewDecision & {
  jira_synced_at?: string | null;
  updated_at?: string;
};

export type RunDecisions = Record<string, ServerDecision>;

export class ReviewApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`Review-API (${status}): ${detail}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { getToken } = await import("./auth");
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch { /* not json */ }
    throw new ReviewApiError(res.status, detail);
  }
  // 204 No Content (DELETE) -- nichts zu parsen
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

export const reviewApi = {
  /** Alle Entscheidungen eines Runs aus dem Backend. */
  list: (runId: string) =>
    request<{ run_id: string; decisions: RunDecisions }>(
      `/api/review/decisions/${encodeURIComponent(runId)}`
    ),

  /** Upsert einer Entscheidung. `reviewed_by` setzt das Backend aus dem JWT. */
  put: (runId: string, ticketKey: string, body: { state: string; reject_reason?: string; edits?: { title?: string; description?: string } }) =>
    request<ServerDecision>(
      `/api/review/decisions/${encodeURIComponent(runId)}/${encodeURIComponent(ticketKey)}`,
      { method: "PUT", body: JSON.stringify(body) }
    ),

  /** Reset einer Entscheidung. */
  remove: (runId: string, ticketKey: string) =>
    request<{ deleted: boolean }>(
      `/api/review/decisions/${encodeURIComponent(runId)}/${encodeURIComponent(ticketKey)}`,
      { method: "DELETE" }
    ),
};
